import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {SysGrammarService} from '../../../services/grammar/sys-grammar.service';
import {SystemicGrammarCategoryDto} from '../../../dtos/grammar/sys/systemic-grammar-category-dto';
import {MatTreeNestedDataSource} from '@angular/material/tree';
import {NotificationService} from '../../../services/common/notification.service';
import {SystemicGrammarEntryDto} from '../../../dtos/grammar/sys/systemic-grammar-entry-dto';
import {AuthService} from '../../../services/auth/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {MatTabGroup} from '@angular/material/tabs';
import {NewGrammarCategoryComponent} from '../new-grammar-category/new-grammar-category.component';
import {MatDialog} from '@angular/material/dialog';
import {NewGrammarEntryComponent} from '../new-grammar-entry/new-grammar-entry.component';
import {FormOptionsLists} from '../../../shared/FormOptionsLists';
import {EventData} from "../../common/category-tree/category-tree.component";
import {TableComponent} from "../../common/table/table.component";
import {SearchTriggerObject} from "../../../shared/SearchTrigger";
import {HttpParams} from "@angular/common/http";
import {filter} from "rxjs";
import {SharedService} from "../../../services/common/shared.service";
import {MarkdownEditorComponent} from "../../common/markdown-editor/markdown-editor.component";

@Component({
  selector: 'app-sys-grammar',
  templateUrl: './sys-grammar.component.html',
  styleUrls: ['./sys-grammar.component.scss'],
  standalone: false
})
export class SysGrammarComponent implements OnInit {

  @ViewChild(MarkdownEditorComponent) markdownEditor!: MarkdownEditorComponent;
  @ViewChild(TableComponent) tableC: TableComponent;
  @ViewChild('tabs') tabGroup: MatTabGroup;

  @Input() params: HttpParams;

  dataSource = new MatTreeNestedDataSource<SystemicGrammarCategoryDto>();
  categoryControl: number = null;
  displayEditingTools = false;

  // checkboxes to add entries to a Sammelmappe, off until the user asks for them
  selectMode = false;

  toggleSelectMode(): void {
    this.selectMode = !this.selectMode;
  }


  displayedColumns: string[];
  resultsLength = 20;


  details: [string, any][];

  categoryExplanation: string = null;
  originalExplanation: string;

  isAuthorized: boolean;
  isAdmin: boolean;

  formOptionsLists = new FormOptionsLists();
  expandedElement: SystemicGrammarEntryDto | null;

  reloadRootsTrigger: boolean = false;
  searchTrigger: SearchTriggerObject = {trigger: false, params: new HttpParams()}
  disableTable: boolean = false;
  resetCategoryControl = false;
  resetSearchTrigger = false;

  entryId: number = null;
  categoryId: number = null;
  initialSearchQuery: string = null;

  constructor(protected sysGrammarService: SysGrammarService,
              protected authService: AuthService,
              private route: ActivatedRoute,
              private router: Router,
              private dialog: MatDialog,
              private notificationService: NotificationService,
              private sharedService: SharedService) {
  }

  ngOnInit() {
    [this.isAuthorized, this.isAdmin] = this.authService.isAuthorizedAndAdmin();
    this.reloadRootsTrigger = !this.reloadRootsTrigger;
    if (this.displayEditingTools === true && this.isAdmin) {
      this.displayedColumns = this.formOptionsLists.displayedColumnsSysGrammarEditing;
    } else {
      this.displayedColumns = this.formOptionsLists.displayedColumnsSysGrammarDetails;
    }
    this.route.queryParams
      .pipe(filter(params => !!params['id'] || !!params['c_id'] || !!params['q']))
      .subscribe((params: HttpParams) => {
        if (params['q']) {
          this.initialSearchQuery = params['q'];
          // clear any previously selected category so it doesn't narrow this deep-linked search
          this.resetCategoryControl = !this.resetCategoryControl;
          this.categoryControl = null;
        }
        this.entryId = params['id'];
        if (this.entryId) {
          // clear any previously selected category so it doesn't hide this deep-linked entry
          this.resetCategoryControl = !this.resetCategoryControl;
          this.categoryControl = null;
          let httpParams = new HttpParams();
          httpParams = httpParams.set('id', this.entryId);
          this.handleSearch(httpParams)
        }
        // category tree is all handled in the tree component
        this.categoryId = params['c_id'];
        if (this.categoryId) {
          // clear the rest of the filter so it doesn't combine with the deep-linked category
          this.resetSearchTrigger = !this.resetSearchTrigger;
          if (this.tableC) {
            this.tableC.sort.active = 'created';
            this.tableC.paginator.pageSize = 25;
            this.tableC.paginator.pageIndex = 0;
          }
        }
        this.sharedService.clearIdParam(this.router);
      });
  }


  saveChanges() {
    this.sysGrammarService.updateExplanation(this.categoryControl, this.markdownEditor.getMarkdown()).subscribe({
      next: (data) => {
        this.originalExplanation = data;
        this.notificationService.openSnackBar('Erklärung erfolgreich gespeichert.');
      },
      error: (error) => {
        this.notificationService.openSnackBar(error);
      }
    });
  }

  toggleEditingTools(change: boolean) {
    if (change) {
      this.displayEditingTools = !this.displayEditingTools;
    }
    if (this.displayEditingTools) {
      this.displayedColumns = this.formOptionsLists.displayedColumnsSysGrammarEditing;
    } else {
      this.displayedColumns = this.formOptionsLists.displayedColumnsSysGrammarDetails;
    }
  }


  getCategoryExplanation() {
    if (this.categoryControl) {
      this.sysGrammarService.getCategoryExplanation(this.categoryControl).subscribe(
        expl => {
          if (expl !== 'no explanation available') {
            this.originalExplanation = expl;
          } else {
            this.originalExplanation = '';
            this.categoryExplanation = null;
          }
        }, error => {
          this.notificationService.openSnackBar(error);
        });
      if (!this.categoryExplanation && !this.displayEditingTools) {
        this.tabGroup.selectedIndex = 0;
      }
    } else {
      this.originalExplanation = '';
      this.categoryExplanation = null;
    }
  }

  openNewCategoryDialog() {
    const dialogRef = this.dialog.open(NewGrammarCategoryComponent);
    dialogRef.afterClosed().subscribe(() => {
      this.reloadRootsTrigger = !this.reloadRootsTrigger;
    });
  }

  openNewEntryDialog() {
    this.dialog.open(NewGrammarEntryComponent);
  }

  /*-------------------- Category Tree --------------------*/

  handleChildSearch(data: EventData) {
    if (this.categoryControl !== data.categoryControl) {
      this.categoryControl = data.categoryControl;
      this.getCategoryExplanation();
      // clear any previously deep-linked/searched entry id so it doesn't stay combined with the new category
      this.entryId = null;
      this.resetSearchTrigger = !this.resetSearchTrigger;
      this.searchTrigger.trigger = !this.searchTrigger.trigger;
    }
    this.getCategoryExplanation();
  }


  handleSetTab($event: number) {
    this.disableTable = $event === 1;
    this.tabGroup.selectedIndex = $event;
  }

  /*-------------------- Search Bar --------------------*/
  handleSearch(params: HttpParams) {
    this.searchTrigger.params = params
  }

  handleResetFilter() {
    this.tableC.sort.active = 'created';
    this.tableC.paginator.pageSize = 25;
    this.tableC.paginator.pageIndex = 0;
    this.resetCategoryControl = !this.resetCategoryControl
    this.categoryControl = null
  }
}
